/**
 * Pill primitive — TASK 7.1.
 *
 * The mock's NON-interactive rounded badge, in the six flavours it actually
 * uses:
 *
 * | tone          | mock source                                | treatment                    |
 * |---------------|--------------------------------------------|------------------------------|
 * | `kind`        | L205 ALBUM/PLAYLIST, L518 type, L619 tag   | accent fill, ink border/text |
 * | `role`        | L634 ADMIN/MEMBER, L664 admin table        | accent fill, ink text        |
 * | `status`      | L665 ACTIVE/INVITED/REMOVED                | ink fill, coloured border+text|
 * | `gone`        | L206 "⊘ GONE"                              | ink fill, yellow text        |
 * | `badge`       | L308/L404/L487 track badges                | ink fill, yellow text        |
 * | `offline`     | L92 "⌁ OFFLINE" header badge               | yellow fill, hard shadow     |
 *
 * §8.5 compliance: every tone that communicates a STATE (`status`, `gone`,
 * `badge`, `offline`) renders an ICON plus a TEXT label in addition to its
 * fill/border, so the state is never carried by colour alone. `Pill()`
 * enforces that at runtime — a status tone without a label throws.
 *
 * These are decorative/informative, not interactive, so the 44px hit-area
 * rule does not apply (it governs interactive primitives). When a pill needs
 * to be tappable the mock uses a Chip or Button instead.
 *
 * @module
 */

import { classes, el } from './dom.js';

/** tone -> [extra classes, default leading icon] */
const TONE = {
  kind: { className: undefined, icon: undefined },
  role: { className: 'sm-pill--role', icon: undefined },
  status: { className: 'sm-pill--status', icon: undefined },
  gone: { className: 'sm-pill--gone', icon: '⊘' },
  badge: { className: 'sm-pill--badge', icon: undefined },
  offline: { className: 'sm-pill--offline', icon: '⌁' },
  failMode: { className: 'sm-pill--failmode', icon: undefined },
  sync: { className: 'sm-pill--sync', icon: undefined },
};

/**
 * @param {{
 *   label: string,
 *   tone?: keyof typeof TONE,
 *   icon?: string,
 *   color?: string,
 *   background?: string,
 *   testId?: string,
 *   role?: string,
 *   className?: string|string[],
 * }} options
 * @returns {HTMLElement}
 */
export function Pill(options) {
  const { label, tone = 'kind', icon, color, background, testId, role, className } = options;

  const preset = TONE[tone];
  if (preset === undefined) throw new Error(`Pill: unknown tone "${String(tone)}"`);

  if (typeof label !== 'string' || label.trim() === '') {
    throw new Error(
      `Pill: tone "${tone}" requires a text label — §8.5 forbids conveying state by colour alone`,
    );
  }

  const glyph = icon ?? preset.icon;

  /** @type {Record<string, string|number|boolean|null|undefined>} */
  const attrs = {};
  if (role !== undefined) attrs.role = role;

  return el('span', {
    className: classes(
      'sm-pill',
      preset.className,
      ...(Array.isArray(className) ? className : classes(className)),
    ),
    text: glyph === undefined ? label : `${glyph} ${label}`,
    testId,
    attrs,
    // Per-item accents (the hashed role/type colours, the per-status border)
    // are data-driven in the mock — CSSOM assignment, never a style string.
    styleProps: {
      // `status` is the one tone where the mock paints the BORDER with the
      // status colour too (L665) — everywhere else the border stays ink.
      ...(color === undefined
        ? {}
        : tone === 'status'
          ? { color, 'border-color': color }
          : { color }),
      ...(background === undefined ? {} : { background }),
    },
  });
}
